import React, { useCallback, useEffect, useState } from "react";
import { Download, Eye, Loader2, Pencil, Search, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { fmtDate, useLang } from "../i18n";
import DateInput from "./DateInput";

export default function FormRecordsList({ formType, onEdit, refresh = null, testid }) {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [draft, setDraft] = useState({ q: "", status: "active", from_date: "", to_date: "" });
  const [applied, setApplied] = useState(draft);
  const [viewDoc, setViewDoc] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { form_type: formType };
      if (applied.q) params.q = applied.q;
      if (applied.from_date) params.from_date = applied.from_date;
      if (applied.to_date) params.to_date = applied.to_date;
      if (applied.status !== "active") params.include_inactive = "1";
      const { data } = await api.get("/forms", { params });
      setItems(applied.status === "inactive" ? data.filter((x) => x.active === false) : data);
    } catch (e) {
      toast.error(apiError(e));
      setItems([]);
    }
  }, [formType, applied]);

  useEffect(() => { load(); }, [load, refresh]);

  const openView = async (id) => {
    try {
      const { data } = await api.get(`/forms/${id}`);
      setViewDoc(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/forms/${id}`);
      setViewDoc(null);
      onEdit(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const renderVal = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? t("common.yes") : t("common.no");
    if (Array.isArray(v)) {
      if (!v.length) return "—";
      return v.map((x) => (x && typeof x === "object"
        ? Object.entries(x).filter(([, val]) => val !== "" && val != null).map(([k, val]) => `${k}: ${val}`).join(", ")
        : String(x))).join("  |  ");
    }
    if (typeof v === "object") {
      const s = Object.entries(v).filter(([, val]) => val !== "" && val != null).map(([k, val]) => `${k}: ${val}`).join(", ");
      return s || "—";
    }
    return String(v);
  };

  return (
    <div className="ec-card overflow-hidden" data-testid={`${testid}-records-list`}>
      <div className="p-4 border-b border-slate-100 space-y-3">
        <h2 className="font-heading font-bold text-slate-900">{t("frl.title")}</h2>
        <div className="ec-filter-bar" data-testid={`${testid}-rl-filters`}>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className="ec-label">{t("h.name_title")}</label>
            <input className="ec-input" value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })}
                   placeholder={t("frl.search_ph")} data-testid={`${testid}-rl-q`} />
          </div>
          <div className="lg:col-span-2">
            <label className="ec-label">{t("frl.status")}</label>
            <select className="ec-input" value={draft.status}
                    onChange={(e) => { const s = e.target.value; setDraft({ ...draft, status: s }); setApplied({ ...draft, status: s }); }}
                    data-testid={`${testid}-rl-status`}>
              <option value="active">{t("frl.st_active")}</option>
              <option value="inactive">{t("frl.st_inactive")}</option>
              <option value="all">{t("frl.st_all")}</option>
            </select>
          </div>
          <div className="lg:col-span-2">
            <label className="ec-label">{t("frl.from")}</label>
            <DateInput value={draft.from_date} onChange={(e) => setDraft({ ...draft, from_date: e.target.value })} data-testid={`${testid}-rl-from`} />
          </div>
          <div className="lg:col-span-2">
            <label className="ec-label">{t("frl.to")}</label>
            <DateInput value={draft.to_date} onChange={(e) => setDraft({ ...draft, to_date: e.target.value })} data-testid={`${testid}-rl-to`} />
          </div>
          <button onClick={() => setApplied(draft)} data-testid={`${testid}-rl-search`} className="ec-filter-btn sm:col-span-2 lg:col-span-1">
            <Search className="w-4 h-4" /> {t("common.search")}
          </button>
        </div>
      </div>

      {items === null ? (
        <div className="p-8 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400" data-testid={`${testid}-rl-empty`}>{t("h.empty")}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="bg-[#16335F] text-white text-left">
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("h.number")}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("h.name_title")}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("common.user")}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("common.date")}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("frl.status")}</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className={`border-t border-slate-100 hover:bg-slate-50 ${item.active === false ? "opacity-50" : ""}`} data-testid={`${testid}-row-${item.form_number}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">
                    {item.form_number}
                    {item.client_id && <div className="text-[10px] text-[#033A8C]">{item.client_id}</div>}
                  </td>
                  <td className="px-4 py-2.5 font-semibold text-slate-800">{item.title}</td>
                  <td className="px-4 py-2.5 text-slate-600">{item.user_name}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{fmtDate(item.created_at)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${item.active !== false ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid={`${testid}-status-${item.form_number}`}>
                      {item.active !== false ? t("u.active") : t("u.inactive")}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap space-x-3">
                    <button onClick={() => openView(item.id)} data-testid={`${testid}-view-${item.form_number}`} className="text-slate-500 hover:text-slate-800 font-semibold text-xs inline-flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5" /> {t("frl.view")}
                    </button>
                    <button onClick={() => openEdit(item.id)} data-testid={`${testid}-edit-${item.form_number}`} className="text-[#033A8C] hover:text-[#044CBC] font-semibold text-xs inline-flex items-center gap-1">
                      <Pencil className="w-3.5 h-3.5" /> {t("common.edit")}
                    </button>
                    <button onClick={() => downloadFile(`/forms/${item.id}/pdf`, `${item.form_number}.pdf`)} data-testid={`${testid}-pdf-${item.form_number}`} className="text-[#044CBC] hover:text-[#033A8C] font-semibold text-xs inline-flex items-center gap-1">
                      <Download className="w-3.5 h-3.5" /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid={`${testid}-view-modal`}>
          <div className="ec-card w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-extrabold text-slate-900">{t("frl.detail")}</h3>
                <div className="text-xs font-mono text-[#033A8C]">{viewDoc.form_number}</div>
              </div>
              <button onClick={() => setViewDoc(null)} data-testid={`${testid}-view-close`} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {Object.entries(viewDoc.data || {}).filter(([k]) => k !== "signature").map(([k, v]) => (
                <div key={k}>
                  <dt className="ec-label">{k.replace(/_/g, " ")}</dt>
                  <dd className="text-xs text-slate-700 break-words">{renderVal(v)}</dd>
                </div>
              ))}
            </dl>
            {viewDoc.data?.signature && <p className="text-xs text-emerald-700 font-semibold">{t("frl.signature_done")}</p>}
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <button onClick={() => openEdit(viewDoc.id)} data-testid={`${testid}-view-edit`} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#033A8C]">
                <Pencil className="w-3.5 h-3.5" /> {t("common.edit")}
              </button>
              <button onClick={() => downloadFile(`/forms/${viewDoc.id}/pdf`, `${viewDoc.form_number}.pdf`)} data-testid={`${testid}-view-pdf`} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-xs font-bold hover:bg-[#033A8C]">
                <Download className="w-3.5 h-3.5" /> {t("common.download_pdf")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
